import { Asterisk, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "./motion";
import { useLang, useT } from "@/i18n/LangContext";

const go = (href) => (e) => {
  e.preventDefault();
  scrollToId(href);
};

export default function Footer() {
  const t = useT();
  const lang = useLang();

  const LINKS = [
    { label: t.nav.services, href: "#services", id: "services" },
    { label: t.nav.approach, href: "#approach", id: "approach" },
    { label: t.nav.studio, href: "#studio", id: "studio" },
    { label: t.nav.contact, href: "#contact", id: "contact" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-ink text-paper">
      <div className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
        <div className="flex flex-col gap-16 md:flex-row md:items-end md:justify-between">
          <div>
            <p
              data-testid="footer-wordmark"
              className="flex flex-wrap items-baseline gap-x-4 gap-y-2"
            >
              <span className="flex items-center gap-2 font-serif text-5xl font-light tracking-tighter md:text-7xl">
                Encraft
                <Asterisk
                  className="h-10 w-10 text-bee md:h-14 md:w-14"
                  aria-hidden="true"
                />
              </span>
              <span className="text-xs font-medium uppercase tracking-[0.35em] text-paper/75 md:text-sm">
                Studio
              </span>
            </p>
            <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-paper/75">
              {t.footer.descriptor}
            </p>
          </div>
          <nav aria-label={t.footer.navLabel}>
            <ul className="flex flex-col gap-4">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.id}`}
                    onClick={go(l.href)}
                    className="text-sm font-medium text-paper/85 transition-colors duration-300 hover:text-bee"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to={lang === "fi" ? "/fi/privacy" : "/privacy"}
                  data-testid="footer-privacy-link"
                  className="text-sm font-medium text-paper/85 transition-colors duration-300 hover:text-bee"
                >
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <span
                  data-testid="footer-terms-coming"
                  className="text-sm text-paper/60"
                >
                  {t.footer.termsComing}
                </span>
              </li>
              <li>
                <a
                  data-testid="footer-email-link"
                  href="mailto:hello@encraft.studio"
                  className="mt-2 inline-block text-sm font-medium text-paper underline decoration-bee decoration-2 underline-offset-4"
                >
                  hello@encraft.studio
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-linkedin-link"
                  href="https://www.linkedin.com/in/liisahindikainen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 flex items-center gap-2 text-sm font-medium text-paper/85 transition-colors duration-300 hover:text-bee"
                >
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                  LinkedIn
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <div className="mt-20 flex flex-col gap-3 border-t border-paper/15 pt-8 text-xs text-paper/60 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {t.footer.rights}</p>
          <p>{t.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
}
